import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Users, Activity, Package, FileText, AlertTriangle, CheckCircle,
  XCircle, Clock, ShieldCheck, RefreshCw, TrendingUp, Building2,
  CreditCard, CalendarClock, FlaskConical, Layers, BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import dashboardService from '../services/dashboardService';

function getUserRole() {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return (u.roleName || '').toLowerCase();
  } catch { return ''; }
}

// ─── Shared stat card ────────────────────────────────────────────────────────
function StatCard({ title, value, description, icon: Icon, color, bgColor }) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardDescription className="text-sm font-medium">{title}</CardDescription>
        <div className={`p-2 ${bgColor} rounded-lg`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-foreground">{value}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────
function Empty({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
      <Icon className="h-10 w-10 mb-2 opacity-30" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

// ─── ADMIN DASHBOARD ─────────────────────────────────────────────────────────
function AdminDashboard({ data, onRefresh }) {
  const stats = [
    { title: 'Total Users',          value: data.totalUsers ?? 0,          icon: Users,        color: 'text-blue-600',    bgColor: 'bg-blue-50',    description: 'All registered users' },
    { title: 'Active Users',         value: data.activeUsers ?? 0,         icon: Activity,     color: 'text-green-600',   bgColor: 'bg-green-50',   description: 'Currently active accounts' },
    { title: 'Organizations',        value: data.totalOrgs ?? 0,           icon: Building2,    color: 'text-purple-600',  bgColor: 'bg-purple-50',  description: 'Registered organizations' },
    { title: 'Operators',            value: data.totalOperators ?? 0,      icon: Users,        color: 'text-indigo-600',  bgColor: 'bg-indigo-50',  description: 'Total operators in system' },
    { title: 'Active Subscriptions', value: data.activeSubscriptions ?? 0, icon: CreditCard,   color: 'text-emerald-600', bgColor: 'bg-emerald-50', description: 'Currently active plans' },
    { title: 'Expiring Soon',        value: data.expiringSoon ?? 0,        icon: CalendarClock,color: 'text-orange-600',  bgColor: 'bg-orange-50',  description: 'Subscriptions expiring in 30 days' },
  ];

  const recentPayments = data.recentPayments ?? [];
  const expiringSoonList = data.expiringSoonList ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Platform-wide overview and system health</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* Tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payments */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Payments</CardTitle>
            <CardDescription>Last 5 payment transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {recentPayments.length > 0 ? (
              <div className="space-y-3">
                {recentPayments.map((p, i) => (
                  <div key={p.paymentHistoryId ?? i} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/40 transition-colors">
                    <div>
                      <p className="text-sm font-medium">{p.organizationName}</p>
                      <p className="text-xs text-muted-foreground">{p.planName} · {new Date(p.paymentDate).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-600">${Number(p.amount).toFixed(2)}</p>
                      <Badge variant={p.paymentStatus === 'Completed' ? 'default' : 'secondary'} className="text-xs mt-1">
                        {p.paymentStatus}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={CreditCard} label="No recent payments" />
            )}
          </CardContent>
        </Card>

        {/* Expiring Subscriptions */}
        <Card>
          <CardHeader>
            <CardTitle>Expiring Subscriptions</CardTitle>
            <CardDescription>Organizations expiring within 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            {expiringSoonList.length > 0 ? (
              <div className="space-y-3">
                {expiringSoonList.map((s, i) => (
                  <div key={s.organizationSubscriptionId ?? i} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/40 transition-colors">
                    <div>
                      <p className="text-sm font-medium">{s.organizationName}</p>
                      <p className="text-xs text-muted-foreground">{s.planName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Expires {new Date(s.endDate).toLocaleDateString()}</p>
                      <Badge variant={s.daysLeft <= 7 ? 'destructive' : 'secondary'} className="text-xs mt-1">
                        {s.daysLeft}d left
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={CalendarClock} label="No subscriptions expiring soon" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── OPERATOR DASHBOARD ───────────────────────────────────────────────────────
function OperatorDashboard({ general, compliance, onRefresh }) {
  // Map from DashboardDto (general) + ComplianceDashboardDto (compliance)
  const lp     = general?.licensePermitSummary ?? {};
  const comp   = general?.compliance ?? {};
  const inv    = compliance?.inventorySummary ?? {};
  const batch  = compliance?.batchSummary ?? {};
  const audit  = compliance?.auditSummary ?? {};
  const docs   = compliance?.documentSummary ?? {};

  const stats = [
    { title: 'Active Licenses',       value: lp.activeLicenses ?? 0,                     icon: ShieldCheck,   color: 'text-green-600',   bgColor: 'bg-green-50',   description: 'Currently active licenses' },
    { title: 'Expiring Licenses',     value: lp.expiringLicenses ?? 0,                   icon: AlertTriangle, color: 'text-orange-600',  bgColor: 'bg-orange-50',  description: 'Expire within 30 days' },
    { title: 'Compliance Score',      value: `${compliance?.complianceScore ?? 0}%`,      icon: CheckCircle,   color: 'text-emerald-600', bgColor: 'bg-emerald-50', description: 'Overall compliance rating' },
    { title: 'Operators Accessible',  value: general?.totalOperatorsAccessible ?? 0,      icon: Users,         color: 'text-blue-600',    bgColor: 'bg-blue-50',    description: 'Operators in your network' },
    { title: 'Inventory Items',       value: inv.totalInventories ?? 0,                   icon: Package,       color: 'text-purple-600',  bgColor: 'bg-purple-50',  description: 'Total inventory records' },
    { title: 'Unread Notifications',  value: general?.unreadNotifications ?? 0,           icon: Activity,      color: 'text-indigo-600',  bgColor: 'bg-indigo-50',  description: 'Pending notifications' },
  ];

  const quickStats = [
    { label: 'Compliance Docs',   value: comp.totalDocuments ?? 0,  icon: FileText,    color: 'text-blue-600',   bgColor: 'bg-blue-50' },
    { label: 'Approved',          value: comp.approved ?? 0,        icon: CheckCircle, color: 'text-green-600',  bgColor: 'bg-green-50' },
    { label: 'Pending Review',    value: comp.pending ?? 0,         icon: Clock,       color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    { label: 'Rejected',          value: comp.rejected ?? 0,        icon: XCircle,     color: 'text-red-600',    bgColor: 'bg-red-50' },
  ];

  // Pie: compliance health
  const complianceHealthData = comp.totalDocuments > 0 ? [
    { name: 'Approved',  value: comp.approved ?? 0,  color: '#10b981' },
    { name: 'Pending',   value: comp.pending ?? 0,   color: '#f59e0b' },
    { name: 'Rejected',  value: comp.rejected ?? 0,  color: '#ef4444' },
    { name: 'Expiring',  value: comp.expiringSoon ?? 0, color: '#6366f1' },
  ].filter(d => d.value > 0) : [];

  // Bar: batch test results
  const batchData = batch.totalBatches > 0 ? [
    { name: 'Passed',  value: batch.passedTests ?? 0,  fill: '#10b981' },
    { name: 'Pending', value: batch.pendingTests ?? 0, fill: '#f59e0b' },
    { name: 'Failed',  value: batch.failedTests ?? 0,  fill: '#ef4444' },
  ] : [];

  // Trends from general (month over month)
  const trends = (general?.trends ?? []).map(t => ({
    month: new Date(t.month).toLocaleDateString('en-US', { month: 'short' }),
    Approved: t.approved,
    Pending:  t.pending,
    Rejected: t.rejected,
  }));

  const topRisks    = general?.topRisks ?? [];
  const expiringDocs = general?.expiringDocuments ?? [];
  const riskLevel   = general?.riskLevel ?? 'N/A';
  const riskScore   = general?.riskScore ?? 0;

  const riskColor = riskLevel === 'High' ? 'text-red-600 bg-red-50 border-red-200'
    : riskLevel === 'Medium' ? 'text-orange-600 bg-orange-50 border-orange-200'
    : 'text-green-600 bg-green-50 border-green-200';

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard Overview</h1>
          <p className="text-muted-foreground mt-1">Monitor your cannabis operations and compliance status</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${riskColor}`}>
            Risk: {riskLevel} ({riskScore}%)
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {/* Main stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* Document quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="text-2xl font-bold mt-0.5">{s.value}</p>
              </div>
              <div className={`p-3 ${s.bgColor} rounded-lg`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compliance Health pie */}
        <Card>
          <CardHeader>
            <CardTitle>Compliance Health</CardTitle>
            <CardDescription>Document status breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            {complianceHealthData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={complianceHealthData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={4} dataKey="value">
                      {complianceHealthData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {complianceHealthData.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span>{item.name}</span>
                      </div>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty icon={CheckCircle} label="No compliance documents yet" />
            )}
          </CardContent>
        </Card>

        {/* Compliance Trends area chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Compliance Trends</CardTitle>
            <CardDescription>Monthly document activity</CardDescription>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="gApproved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gPending" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="Approved" stroke="#10b981" fill="url(#gApproved)" />
                  <Area type="monotone" dataKey="Pending"  stroke="#f59e0b" fill="url(#gPending)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty icon={TrendingUp} label="No trend data available yet" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Batch results & Audit row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Batch test results */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Batch Test Results</CardTitle>
            <CardDescription>Lab COA pass / fail summary</CardDescription>
          </CardHeader>
          <CardContent>
            {batchData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={batchData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {batchData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty icon={FlaskConical} label="No batch data available yet" />
            )}
            {batch.totalBatches > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-lg bg-emerald-50 py-2">
                  <p className="font-bold text-emerald-700 text-lg">{batch.passedTests}</p>
                  <p className="text-emerald-600">Passed</p>
                </div>
                <div className="rounded-lg bg-yellow-50 py-2">
                  <p className="font-bold text-yellow-700 text-lg">{batch.pendingTests}</p>
                  <p className="text-yellow-600">Pending</p>
                </div>
                <div className="rounded-lg bg-red-50 py-2">
                  <p className="font-bold text-red-700 text-lg">{batch.failedTests}</p>
                  <p className="text-red-600">Failed</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Audit Summary</CardTitle>
            <CardDescription>Compliance audits and corrective actions</CardDescription>
          </CardHeader>
          <CardContent>
            {audit.totalAudits > 0 ? (
              <div className="grid grid-cols-3 gap-4 text-center mt-4">
                <div className="rounded-xl border p-4">
                  <p className="text-3xl font-bold text-blue-600">{audit.totalAudits}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total Audits</p>
                </div>
                <div className="rounded-xl border p-4">
                  <p className="text-3xl font-bold text-red-600">{audit.violations}</p>
                  <p className="text-xs text-muted-foreground mt-1">Violations</p>
                </div>
                <div className="rounded-xl border p-4">
                  <p className="text-3xl font-bold text-orange-600">{audit.openCorrectiveActions}</p>
                  <p className="text-xs text-muted-foreground mt-1">Open Actions</p>
                </div>
              </div>
            ) : (
              <Empty icon={BookOpen} label="No audit data available yet" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risks & Expiring docs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Risks */}
        <Card>
          <CardHeader>
            <CardTitle>Risk Alerts</CardTitle>
            <CardDescription>Active compliance risks</CardDescription>
          </CardHeader>
          <CardContent>
            {topRisks.length > 0 ? (
              <div className="space-y-3">
                {topRisks.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/40 transition-colors">
                    <div className="mt-0.5 shrink-0">
                      {r.severity === 'High'   && <AlertTriangle className="h-5 w-5 text-red-600" />}
                      {r.severity === 'Medium' && <AlertTriangle className="h-5 w-5 text-orange-500" />}
                      {r.severity === 'Low'    && <CheckCircle   className="h-5 w-5 text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium truncate">{r.title}</p>
                        <Badge variant={r.severity === 'High' ? 'destructive' : r.severity === 'Medium' ? 'secondary' : 'outline'} className="text-xs shrink-0">
                          {r.severity}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={CheckCircle} label="No risk alerts" />
            )}
          </CardContent>
        </Card>

        {/* Expiring Documents */}
        <Card>
          <CardHeader>
            <CardTitle>Expiring Documents</CardTitle>
            <CardDescription>Documents expiring within 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            {expiringDocs.length > 0 ? (
              <div className="space-y-3">
                {expiringDocs.slice(0, 6).map((d, i) => (
                  <div key={d.complianceDocumentId ?? i} className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/40 transition-colors">
                    <div>
                      <p className="text-sm font-medium">{d.documentType}</p>
                      <p className="text-xs text-muted-foreground">Expires {new Date(d.expiryDate).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">{d.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon={Layers} label="No documents expiring soon" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const [isLoading, setIsLoading]   = useState(true);
  const [adminData, setAdminData]   = useState(null);
  const [generalData, setGeneral]   = useState(null);
  const [complianceData, setComp]   = useState(null);
  const isAdmin = getUserRole() === 'admin';

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      if (isAdmin) {
        const d = await dashboardService.getAdminDashboard();
        setAdminData(d);
      } else {
        const [gen, comp] = await Promise.allSettled([
          dashboardService.getOperatorDashboard(),
          dashboardService.getComplianceDashboard(),
        ]);
        if (gen.status === 'fulfilled')  setGeneral(gen.value);
        if (comp.status === 'fulfilled') setComp(comp.value);
      }
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {isAdmin
        ? <AdminDashboard data={adminData ?? {}} onRefresh={fetchAll} />
        : <OperatorDashboard general={generalData} compliance={complianceData} onRefresh={fetchAll} />
      }
    </DashboardLayout>
  );
}
